import React, { useState } from "react";
import { Phone, MessageCircle, Instagram, Linkedin, Youtube, MapPin, Mail, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { PHONES, WHATSAPP_URL, SOCIALS, LOCATIONS, BRAND } from "@/constants/content";

export default function ContactSection({ heading = "Get in Touch", subheading = "Let's bring expert care to your home.", compact = false }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", subject: "", message: "" });
  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.message) {
      toast.error("Please fill name, phone, and message.");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.email) delete payload.email;
      if (!payload.subject) delete payload.subject;
      await api.post("/contacts", payload);
      toast.success("Message sent! Our team will reach out shortly.");
      setForm({ name: "", phone: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className={compact ? "py-14" : "section"} data-testid="contact-section">
      <div className="container-page">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          <div>
            <div className="eyebrow mb-3">{heading}</div>
            <h2 className="font-display font-bold text-slate-900 text-3xl md:text-4xl lg:text-5xl tracking-tight">
              {subheading.split(" ").slice(0, -2).join(" ")}{" "}
              <span className="bg-gradient-to-r from-[#0A84FF] to-[#10B981] bg-clip-text text-transparent">{subheading.split(" ").slice(-2).join(" ")}</span>
            </h2>
            <p className="mt-4 text-slate-600">Reach us by phone, WhatsApp, or this form — our team responds within minutes, 24×7.</p>

            <div className="mt-8 space-y-4">
              <div className="rounded-2xl border border-slate-200/70 p-5 bg-gradient-to-br from-[#0A84FF]/5 to-white">
                <div className="text-xs font-bold uppercase tracking-[0.16em] mb-3 flex items-center gap-1.5" style={{ color: '#0A84FF' }}>
                  <Phone className="h-3.5 w-3.5" /> Call Us 24×7
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {PHONES.map((p) => (
                    <a key={p} href={`tel:${p}`} className="font-display font-bold text-slate-900 hover:text-[#0A84FF] transition-colors" data-testid={`contact-phone-${p}`}>
                      {p}
                    </a>
                  ))}
                </div>
              </div>

              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-between rounded-2xl border border-slate-200/70 p-5 bg-[#25D366]/5 hover:bg-[#25D366]/10 transition-colors"
                data-testid="contact-whatsapp"
              >
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-[#25D366] text-white flex items-center justify-center">
                    <MessageCircle className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">WhatsApp</div>
                    <div className="font-display font-bold text-slate-900">Chat instantly</div>
                  </div>
                </div>
                <Send className="h-4 w-4 text-[#25D366]" />
              </a>

              <a href={`mailto:${BRAND.email}`} className="flex items-center gap-3 rounded-2xl border border-slate-200/70 p-5 hover:border-[#0A84FF]/30 transition-colors">
                <div className="h-10 w-10 rounded-xl bg-slate-900 text-white flex items-center justify-center">
                  <Mail className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-xs text-slate-500">Email us</div>
                  <div className="font-display font-bold text-slate-900">{BRAND.email}</div>
                </div>
              </a>

              <div className="rounded-2xl border border-slate-200/70 p-5">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl text-white flex items-center justify-center" style={{ background: '#0A84FF' }}>
                    <MapPin className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">Service Areas</div>
                    <div className="font-display font-bold text-slate-900">Delhi NCR · Pan-India</div>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {LOCATIONS.map((l) => (
                    <span key={l.slug} className="text-[11px] font-medium px-2 py-1 rounded-md bg-slate-50 text-slate-600 border border-slate-100">
                      {l.name}
                    </span>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <a href={SOCIALS.instagram} target="_blank" rel="noreferrer" className="h-10 w-10 rounded-xl bg-slate-900 text-white flex items-center justify-center hover:bg-[#0A84FF] transition-colors" aria-label="Instagram">
                  <Instagram className="h-5 w-5" />
                </a>
                <a href={SOCIALS.linkedin} target="_blank" rel="noreferrer" className="h-10 w-10 rounded-xl bg-slate-900 text-white flex items-center justify-center hover:bg-[#0A84FF] transition-colors" aria-label="LinkedIn">
                  <Linkedin className="h-5 w-5" />
                </a>
                <a href={SOCIALS.youtube} target="_blank" rel="noreferrer" className="h-10 w-10 rounded-xl bg-slate-900 text-white flex items-center justify-center hover:bg-[#0A84FF] transition-colors" aria-label="YouTube">
                  <Youtube className="h-5 w-5" />
                </a>
              </div>
            </div>

            <div className="mt-8 rounded-2xl overflow-hidden border border-slate-200">
              <iframe
                title="KinFlex Service Area - Delhi NCR"
                src="https://www.google.com/maps?q=Delhi+NCR&output=embed"
                className="w-full h-64"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          <form onSubmit={submit} className="rounded-3xl bg-gradient-to-br from-slate-50 to-white border border-slate-200/70 p-6 md:p-8 shadow-sm" data-testid="contact-form">
            <h3 className="font-display font-bold text-slate-900 text-2xl">Send us a message</h3>
            <p className="text-sm text-slate-500 mt-1">We'll call you within minutes.</p>

            <div className="mt-6 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-semibold">Full Name *</Label>
                  <Input value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Your name" className="mt-1.5" required data-testid="contact-name" />
                </div>
                <div>
                  <Label className="text-xs font-semibold">Phone *</Label>
                  <Input value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="+91 XXXXX XXXXX" className="mt-1.5" required data-testid="contact-phone-input" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-semibold">Email</Label>
                  <Input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="you@email.com" className="mt-1.5" data-testid="contact-email" />
                </div>
                <div>
                  <Label className="text-xs font-semibold">Subject</Label>
                  <Input value={form.subject} onChange={(e) => update("subject", e.target.value)} placeholder="Service enquiry" className="mt-1.5" data-testid="contact-subject" />
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold">Message *</Label>
                <Textarea value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="Tell us about the patient and care needed..." rows={5} className="mt-1.5" required data-testid="contact-message" />
              </div>
              <Button type="submit" disabled={loading} className="w-full rounded-full bg-[#0A84FF] hover:bg-[#0070d9] text-white h-12 font-semibold" data-testid="contact-submit-btn">
                {loading ? "Sending..." : "Send Message"}
                <Send className="h-4 w-4 ml-2" />
              </Button>
              <div className="text-[11px] text-slate-400 text-center">
                Our care coordinator will get back to you shortly.
              </div>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
